// Documentos guardados (pedido de Rodrigo, 2026-10-09; como «Revisar documentos en estado pendiente»
// de Random): todo lo que alguien dejó en borrador —nota de venta (factura o boleta), cotización,
// guía de despacho, orden de compra, recepción, devolución a proveedor— para que otra persona (o la
// misma) lo tome y termine el proceso. Cada tipo aparece solo si el usuario puede verlo.
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { fecha, fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import type { TipoPestana } from '../shell/pestanas';

export interface DocumentoGuardado {
  tipo: string;
  documento: string | null;
  /** Nota de venta: el canal (MESON = venta pendiente del punto de venta). */
  canal?: string | null;
  id: string;
  numero: string | null;
  fecha: string | null;
  contraparte: string | null;
  total: string | null;
  creadoPor: string | null;
  creadoPorNombre: string | null;
  actualizadoPor: string | null;
  actualizadoPorNombre: string | null;
  actualizadoEn: string | null;
  abrir: { tipo: string; params: Record<string, string>; titulo: string };
}

/** El nombre del documento como lo dice el operador (la nota dice si será factura o boleta). */
export function nombreDocumento(d: Pick<DocumentoGuardado, 'tipo' | 'documento'>): string {
  if (d.tipo === 'NOTA_VENTA')
    return d.documento === 'BOLETA'
      ? 'Nota de venta · boleta'
      : d.documento === 'FACTURA'
        ? 'Nota de venta · factura'
        : 'Nota de venta';
  return (
    (
      {
        COTIZACION: 'Cotización',
        GUIA_DESPACHO: 'Guía de despacho',
        ORDEN_COMPRA: 'Orden de compra',
        RECEPCION: 'Recepción de compra',
        DEVOLUCION_PROVEEDOR: 'Devolución a proveedor',
      } as Record<string, string>
    )[d.tipo] ?? d.tipo
  );
}

export function useDocumentosGuardados(empresaId: string, activo = true) {
  return useQuery({
    enabled: activo,
    queryKey: ['emp', empresaId, 'documentos', 'guardados'] as const,
    queryFn: () =>
      api.get<{ items: DocumentoGuardado[] }>('/documentos/guardados', { query: { empresaId } }),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

type Filtro = 'todos' | 'mios';

/** Los guardados de Compras; el resto (nota de venta, cotización, guía) es de Ventas. */
const TIPOS_COMPRAS = new Set(['ORDEN_COMPRA', 'RECEPCION', 'DEVOLUCION_PROVEEDOR']);

export function DocumentosGuardados({
  alAbrir,
  ambito = null,
}: {
  alAbrir: (tipo: TipoPestana, params: Record<string, string>, titulo: string) => void;
  /** Desde el menú de Compras solo compras; desde Ventas solo ventas; sin ámbito, todo. */
  ambito?: 'compras' | 'ventas' | null;
}) {
  const { empresa, usuario } = useSesion();
  const q = useDocumentosGuardados(empresa!.id);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [tipo, setTipo] = useState('');
  const [texto, setTexto] = useState('');
  const todos = useMemo(
    () =>
      (q.data?.items ?? []).filter(
        (d) => !ambito || (ambito === 'compras') === TIPOS_COMPRAS.has(d.tipo),
      ),
    [q.data, ambito],
  );
  const tipos = useMemo(
    () => [...new Map(todos.map((d) => [nombreDocumento(d), d])).keys()].sort(),
    [todos],
  );
  const filas = todos.filter(
    (d) =>
      (filtro === 'todos' || d.creadoPor === usuario?.id) &&
      (!tipo || nombreDocumento(d) === tipo) &&
      (!texto.trim() ||
        `${d.numero ?? ''} ${d.contraparte ?? ''} ${d.creadoPorNombre ?? ''}`
          .toLowerCase()
          .includes(texto.trim().toLowerCase())),
  );
  const abrir = (d: DocumentoGuardado) =>
    alAbrir(
      d.abrir.tipo as TipoPestana,
      // Una nota tomada de los guardados se descarta si no se vuelve a guardar (2026-10-23).
      d.tipo === 'NOTA_VENTA' ? { ...d.abrir.params, tomada: '1' } : d.abrir.params,
      d.abrir.titulo,
    );

  const columnas: Columna<DocumentoGuardado>[] = [
    { id: 'tipo', titulo: 'Documento', ancho: '13rem', celda: (d) => nombreDocumento(d) },
    {
      id: 'numero',
      titulo: 'N°',
      ancho: '9rem',
      celda: (d) => <span className="mono">{d.numero ?? '—'}</span>,
    },
    { id: 'fecha', titulo: 'Fecha', ancho: '7rem', celda: (d) => (d.fecha ? fecha(d.fecha) : '—') },
    { id: 'contraparte', titulo: 'Cliente / proveedor', celda: (d) => d.contraparte ?? '—' },
    {
      id: 'total',
      titulo: 'Total',
      alinear: 'der',
      ancho: '8rem',
      celda: (d) => (d.total !== null ? pesos(d.total) : '—'),
    },
    {
      id: 'guardo',
      titulo: 'Guardado por',
      ancho: '11rem',
      celda: (d) =>
        d.creadoPor && d.creadoPor === usuario?.id
          ? `${usuario.nombre} (tú)`
          : (d.creadoPorNombre ?? '—'),
    },
    {
      id: 'cambio',
      titulo: 'Último cambio',
      ancho: '12rem',
      celda: (d) => (
        <>
          {fechaHora(d.actualizadoEn)}
          {d.actualizadoPorNombre && d.actualizadoPor !== d.creadoPor && (
            <div className="apagado">{d.actualizadoPorNombre}</div>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="pantalla consulta" data-pantalla="documentos-guardados">
      <p className="apagado">
        Documentos que quedaron guardados sin terminar. Cualquiera con permiso puede abrir uno
        (Enter o doble clic) y terminar el proceso; queda registrado quién hizo cada paso.
      </p>
      <div className="acciones-fila">
        <label className="campo">
          Mostrar
          <select
            aria-label="De quién"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value as Filtro)}
          >
            <option value="todos">Todos los guardados</option>
            <option value="mios">Solo los míos</option>
          </select>
        </label>
        <label className="campo">
          Tipo
          <select
            aria-label="Tipo de documento"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
          >
            <option value="">Todos</option>
            {tipos.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          Buscar
          <input
            aria-label="Buscar en guardados"
            placeholder="número, cliente, proveedor o usuario"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Documentos guardados"
        columnas={columnas}
        filas={filas}
        clave={(d) => `${d.tipo}:${d.id}`}
        cargando={q.isLoading}
        vacio="No hay documentos guardados"
        alAbrir={abrir}
        autofoco
      />
    </div>
  );
}
